import cn from 'classnames';
import {FC, ReactNode, useState} from 'react';
import {
	formatBelarusPassportNumberDisplay,
	formatDateDisplay,
	formatDepartmentCodeDisplay,
	formatPassportSeriesNumberDisplay,
	ForeignDocumentDetailsStepValues,
	getForeignDocumentReviewFields,
	PassportDetailsStepValues,
	PersonalInfoStepValues,
	RegistrationGender,
	requiresPassportDepartmentCode,
	requiresPassportIdentificationNumber,
	requiresPassportLatinNames,
	requiresPassportRegistrationAddress,
	requiresRussianNameLabels,
	usesBelarusPassportNumber,
} from '../../model';
import {CountryDropdown} from '../components/CountryDropdown/CountryDropdown';
import {
	ConfirmationResetIcon,
	RegistrationConfirmationModal,
} from '../components/RegistrationConfirmationModal';
import {StepLayout} from 'features/registration';
import {EditIcon} from 'shared/icons';
import {FormField} from 'ui';
import styles from './IdentityReviewStep.module.css';

type ReviewField = {
	label: string;
	value: string;
};

type Props = {
	personalInfo: PersonalInfoStepValues;
	documentPreviewUrls: string[];
	passportDetails: PassportDetailsStepValues;
	foreignDocumentDetails?: ForeignDocumentDetailsStepValues;
	useForeignDocumentDetails?: boolean;
	residencePreviewUrl?: string | null;
	residenceAddress?: string;
	onEditPersonalInfo: () => void;
	/** Редактирование паспорта/документов (фото + поля). */
	onEditDocumentDetails: () => void;
	onEditResidenceDocument?: () => void;
	/** После подтверждения смены гражданства — на «Давайте познакомимся». */
	onRestartFromCitizenship: () => void;
	/** После подтверждения смены резидентства — на «Давайте познакомимся». */
	onRestartFromResidence: () => void;
};

const genderLabels: Record<RegistrationGender, string> = {
	male: 'Мужской',
	female: 'Женский',
};

const formatGender = (gender: RegistrationGender | null): string => {
	return gender ? genderLabels[gender] : '—';
};

const CITIZENSHIP_EDIT_DESCRIPTION = 'После изменения гражданства нужно заполнить профиль заново';
const RESIDENCE_EDIT_DESCRIPTION = 'После изменения страны проживания нужно заполнить профиль заново';

const ReviewSection: FC<{
	title: string;
	onEdit: () => void;
	divided?: boolean;
	children: ReactNode;
}> = ({title, onEdit, divided = false, children}) => (
	<section className={cn(styles.section, divided && styles.section_divided)}>
		<div className={styles.sectionHeader}>
			<h2 className={styles.sectionTitle}>{title}</h2>
			<button type="button" className={styles.editButton} aria-label={`Редактировать: ${title}`} onClick={onEdit}>
				<EditIcon />
			</button>
		</div>
		{children}
	</section>
);

const FieldsList: FC<{fields: ReviewField[]}> = ({fields}) => (
	<dl className={styles.fields}>
		{fields.map((field) => (
			<div key={field.label} className={styles.field}>
				<dt className={styles.label}>{field.label}</dt>
				<dd className={styles.value}>{field.value || '—'}</dd>
			</div>
		))}
	</dl>
);

const PhotosField: FC<{label: string; urls: string[]}> = ({label, urls}) => (
	<div className={styles.field}>
		<p className={styles.label}>{label}</p>
		{urls.length ? (
			<div className={styles.photos}>
				{urls.map((url) => (
					<div key={url} className={styles.photoWrap}>
						<img className={styles.photo} src={url} alt="" />
					</div>
				))}
			</div>
		) : (
			<p className={styles.value}>—</p>
		)}
	</div>
);

export const IdentityReviewStep: FC<Props> = ({
	personalInfo,
	documentPreviewUrls,
	passportDetails,
	foreignDocumentDetails,
	useForeignDocumentDetails = false,
	residencePreviewUrl,
	residenceAddress = '',
	onEditPersonalInfo,
	onEditDocumentDetails,
	onEditResidenceDocument,
	onRestartFromCitizenship,
	onRestartFromResidence,
}) => {
	const [countryEditKind, setCountryEditKind] = useState<'citizenship' | 'residence' | null>(null);

	const useRussianNameLabels = requiresRussianNameLabels(
		personalInfo.citizenshipOksmCode,
		personalInfo.residenceOksmCode,
	);

	const personalFields: ReviewField[] = [
		{label: 'Пол', value: formatGender(personalInfo.gender)},
		{
			label: useRussianNameLabels ? 'Фамилия (на русском)' : 'Фамилия',
			value: personalInfo.secondName.trim(),
		},
		{
			label: useRussianNameLabels ? 'Имя (на русском)' : 'Имя',
			value: personalInfo.firstName.trim(),
		},
		{label: 'Отчество (если есть)', value: personalInfo.patronymic.trim()},
		{label: 'E-mail', value: personalInfo.email.trim()},
	];

	const belarusPassport = usesBelarusPassportNumber(personalInfo.citizenshipOksmCode);

	const passportFields: ReviewField[] = [
		{
			label: belarusPassport ? 'Номер паспорта' : 'Серия и номер',
			value: belarusPassport
				? formatBelarusPassportNumberDisplay(passportDetails.seriesNumber)
				: formatPassportSeriesNumberDisplay(passportDetails.seriesNumber),
		},
		...(requiresPassportIdentificationNumber(personalInfo.citizenshipOksmCode)
			? [{label: 'Идентификационный номер', value: passportDetails.identificationNumber.trim()}]
			: []),
		...(requiresPassportLatinNames(personalInfo.citizenshipOksmCode)
			? [
					{label: 'Фамилия (на латинице)', value: passportDetails.secondNameLatin.trim()},
					{label: 'Имя (на латинице)', value: passportDetails.latinName.trim()},
				]
			: []),
		{label: 'Кем выдан', value: passportDetails.issuer.trim()},
		{label: 'Дата выдачи', value: formatDateDisplay(passportDetails.issuedAt)},
		...(requiresPassportDepartmentCode(personalInfo.citizenshipOksmCode)
			? [
					{
						label: 'Код подразделения',
						value: formatDepartmentCodeDisplay(passportDetails.departmentCode),
					},
				]
			: []),
		{label: 'Дата рождения', value: formatDateDisplay(passportDetails.birthDate)},
		{label: 'Место рождения', value: passportDetails.birthPlace.trim()},
		...(requiresPassportRegistrationAddress(
			personalInfo.residenceOksmCode,
			personalInfo.citizenshipOksmCode,
		)
			? [{label: 'Адрес регистрации', value: passportDetails.registrationAddress.trim()}]
			: []),
	];

	const foreignDocumentFields: ReviewField[] = foreignDocumentDetails
		? getForeignDocumentReviewFields(
				personalInfo.citizenshipOksmCode,
				foreignDocumentDetails,
				personalInfo.residenceOksmCode,
			)
		: [];

	const documentFields = useForeignDocumentDetails ? foreignDocumentFields : passportFields;
	const detailsSectionTitle = useForeignDocumentDetails ? 'Данные документов' : 'Паспортные данные';
	const photosFieldLabel = useForeignDocumentDetails ? 'Фото документов' : 'Фото паспорта';

	return (
		<StepLayout>
			<StepLayout.Title>Давайте всё сверим</StepLayout.Title>

			<StepLayout.Content className={styles.content}>
				<div className={styles.countries}>
					<FormField label="Гражданство">
						<CountryDropdown
							value={personalInfo.citizenshipOksmCode}
							countryName={personalInfo.citizenshipCountryName}
							labelSize="compact"
							placeholder="—"
							onTriggerClick={() => setCountryEditKind('citizenship')}
						/>
					</FormField>
					<FormField label="Страна проживания (налогового резидентства)">
						<CountryDropdown
							value={personalInfo.residenceOksmCode}
							countryName={personalInfo.residenceCountryName}
							labelSize="compact"
							placeholder="—"
							onTriggerClick={() => setCountryEditKind('residence')}
						/>
					</FormField>
				</div>

				<ReviewSection title="Личные данные" onEdit={onEditPersonalInfo}>
					<FieldsList fields={personalFields} />
				</ReviewSection>

				<ReviewSection title={detailsSectionTitle} divided onEdit={onEditDocumentDetails}>
					<PhotosField label={photosFieldLabel} urls={documentPreviewUrls} />
					<FieldsList fields={documentFields} />
				</ReviewSection>

				{residencePreviewUrl || residenceAddress.trim() ? (
					<ReviewSection
						title="Данные места проживания"
						divided
						onEdit={onEditResidenceDocument ?? onEditDocumentDetails}
					>
						{residencePreviewUrl ? (
							<div className={styles.field}>
								<p className={styles.label}>Документ</p>
								<div className={styles.photos}>
									<div className={styles.photoWrap}>
										<img className={styles.photo} src={residencePreviewUrl} alt="" />
									</div>
								</div>
							</div>
						) : null}
						{residenceAddress.trim() ? (
							<div className={styles.field}>
								<p className={styles.label}>Адрес проживания</p>
								<p className={styles.value}>{residenceAddress.trim()}</p>
							</div>
						) : null}
					</ReviewSection>
				) : null}
			</StepLayout.Content>

			<RegistrationConfirmationModal
				isOpen={countryEditKind !== null}
				title="Вы уверены?"
				description={
					countryEditKind === 'residence' ? RESIDENCE_EDIT_DESCRIPTION : CITIZENSHIP_EDIT_DESCRIPTION
				}
				confirmText="Изменить"
				illustration={<ConfirmationResetIcon />}
				onConfirm={() => {
					const kind = countryEditKind;
					setCountryEditKind(null);

					if (kind === 'residence') {
						onRestartFromResidence();
						return;
					}

					onRestartFromCitizenship();
				}}
				onCancel={() => setCountryEditKind(null)}
			/>
		</StepLayout>
	);
};
